import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CookieConsentService } from '../../services/cookie-consent.service';

@Component({
  selector: 'app-cookie-consent-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    @if (consent.bannerVisible()) {
      <div
        class="fixed bottom-0 left-0 right-0 z-50"
        role="dialog"
        [attr.aria-label]="'cookieConsent.ariaLabel' | translate"
      >
        <div class="mx-auto max-w-4xl p-4 md:p-6 m-4 rounded-xl bg-white shadow-2xl border border-gray-200">
          <p class="text-sm text-gray-700 mb-4">
            {{ 'cookieConsent.message' | translate }}
            <a [href]="cookiesPageUrl()" target="_blank" rel="noopener noreferrer" class="underline text-[#002D97] ml-1">
              {{ 'cookieConsent.privacy' | translate }}
            </a>
          </p>

          @if (consent.settingsExpanded()) {
            <div class="mb-4 space-y-3">
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ 'cookieConsent.necessary' | translate }}</p>
                  <p class="text-xs text-gray-500">{{ 'cookieConsent.necessaryDesc' | translate }}</p>
                </div>
                <span class="text-xs font-medium text-gray-400 shrink-0 ml-4">{{ 'cookieConsent.alwaysOn' | translate }}</span>
              </div>
              <label class="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ 'cookieConsent.analytics' | translate }}</p>
                  <p class="text-xs text-gray-500">{{ 'cookieConsent.analyticsDesc' | translate }}</p>
                </div>
                <input
                  type="checkbox"
                  class="w-5 h-5 shrink-0 ml-4 accent-[#002D97] cursor-pointer"
                  [checked]="analyticsChecked()"
                  (change)="analyticsChecked.set($any($event.target).checked)"
                />
              </label>
              <label class="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ 'cookieConsent.marketing' | translate }}</p>
                  <p class="text-xs text-gray-500">{{ 'cookieConsent.marketingDesc' | translate }}</p>
                </div>
                <input
                  type="checkbox"
                  class="w-5 h-5 shrink-0 ml-4 accent-[#002D97] cursor-pointer"
                  [checked]="marketingChecked()"
                  (change)="marketingChecked.set($any($event.target).checked)"
                />
              </label>
            </div>
          }

          <div class="flex flex-wrap gap-3 justify-end">
            @if (!consent.settingsExpanded()) {
              <button
                type="button"
                class="px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                (click)="consent.settingsExpanded.set(true)"
              >
                {{ 'cookieConsent.customize' | translate }}
              </button>
            } @else {
              <button
                type="button"
                class="px-5 py-2.5 text-sm font-medium rounded-lg border border-[#002D97] bg-white text-[#002D97] hover:bg-blue-50 transition-colors cursor-pointer"
                (click)="saveCustom()"
              >
                {{ 'cookieConsent.save' | translate }}
              </button>
            }
            <button
              type="button"
              class="px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              (click)="consent.rejectAll()"
            >
              {{ 'cookieConsent.reject' | translate }}
            </button>
            <button
              type="button"
              class="px-5 py-2.5 text-sm font-medium rounded-lg bg-[#002D97] text-white hover:bg-[#001f6b] transition-colors cursor-pointer"
              (click)="consent.acceptAll()"
            >
              {{ 'cookieConsent.accept' | translate }}
            </button>
          </div>
        </div>
      </div>
    }
  `
})
export class CookieConsentBannerComponent {
  consent = inject(CookieConsentService);
  private translate = inject(TranslateService);

  analyticsChecked = signal(false);
  marketingChecked = signal(false);

  cookiesPageUrl = signal('https://starthub.be/fr/cookies');

  constructor() {
    // Seed toggles from saved preferences each time the banner (re)opens
    effect(() => {
      if (this.consent.bannerVisible()) {
        const saved = this.consent.getConsent();
        this.analyticsChecked.set(!!saved?.analytics);
        this.marketingChecked.set(!!saved?.marketing);
        const lang = this.translate.currentLang === 'nl' ? 'nl' : 'fr';
        this.cookiesPageUrl.set(`https://starthub.be/${lang}/cookies`);
      }
    });
  }

  saveCustom(): void {
    this.consent.save({
      analytics: this.analyticsChecked(),
      marketing: this.marketingChecked()
    });
  }
}
